import React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { selectMetricBannerData } from 'containers/SpearPhishingAgent/selectors'

import Badge from 'components/lib/DesignSystem/V3/Badge'

import BehavioralRiskGauge from './BehavioralRiskGauge'

const BEHAVIORAL_RISK_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

const DEFAULT_BEHAVIORAL_RISK_STATUS = 'high'
const PERIOD_LABEL = 'Last 7 days'

const getBehavioralRiskStatus = (status) => {
  const normalizedStatus = String(status || DEFAULT_BEHAVIORAL_RISK_STATUS).toLowerCase()
  return BEHAVIORAL_RISK_LABELS[normalizedStatus]
    ? normalizedStatus
    : DEFAULT_BEHAVIORAL_RISK_STATUS
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Accent = styled.div`
  width: 5px;
  height: 20px;
  border-radius: var(--t-borders-radius-rounded-lg);
  background: var(--t-colors-yellow-400);
`

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
`

const Title = styled.div`
  color: #ffffff;
  font-size: var(--t-typography-font-size-base);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-base);
`

const PeriodTag = styled(Badge)`
  flex-shrink: 0;
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: flex-end;
  min-width: 0;
  flex: 1 1 auto;
`

const TotalValue = styled.div`
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-4xl);
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 1;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const MetaItem = styled.div`
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-xs);
  white-space: nowrap;
`

const MetaValue = styled.span`
  color: #ffffff;
`

const MetaLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
`

const MetaDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #ffffff;
  flex: 0 0 auto;
`

const BehavioralRiskPlaceholder = () => {
  const metricBannerData = useSelector(selectMetricBannerData)
  const behavioralRiskData = metricBannerData.data?.companyRiskSummary?.behavioralRisk || {}
  const status = getBehavioralRiskStatus(behavioralRiskData.status)
  const serialClickerCount = Math.round(Number(behavioralRiskData.serialClickerCount ?? 0))
  const untrainedCount = Math.round(Number(behavioralRiskData.untrainedCount ?? 0))

  return (
    <Container data-testid="Psima_Dashboard_TopBanner_BehavioralRisk_Placeholder">
      <Header>
        <Accent />
        <HeaderMain>
          <Title data-testid="Psima_Dashboard_TopBanner_BehavioralRisk_Title_div">
            Behavioral Risk
          </Title>
          <PeriodTag
            dataTestId="Psima_Dashboard_TopBanner_BehavioralRisk_Period_div"
            color="rgba(255, 255, 255, 0.08)"
            size="small"
            textColor="var(--t-colors-gray-400)"
            variant="filled"
          >
            {PERIOD_LABEL}
          </PeriodTag>
        </HeaderMain>
      </Header>

      <ContentRow>
        <LeftColumn>
          <TotalValue data-testid="Psima_Dashboard_TopBanner_BehavioralRisk_Total_div">
            {BEHAVIORAL_RISK_LABELS[status] || status}
          </TotalValue>

          <MetaRow>
            <MetaItem data-testid="Psima_Dashboard_TopBanner_BehavioralRisk_SerialClickers_div">
              <MetaValue>{serialClickerCount}</MetaValue>
              <MetaLabel>{' serial clickers'}</MetaLabel>
            </MetaItem>
            <MetaDot />
            <MetaItem data-testid="Psima_Dashboard_TopBanner_BehavioralRisk_Untrained_div">
              <MetaValue>{untrainedCount}</MetaValue>
              <MetaLabel>{' untrained'}</MetaLabel>
            </MetaItem>
          </MetaRow>
        </LeftColumn>

        <BehavioralRiskGauge
          status={status}
          testIdPrefix="Psima_Dashboard_TopBanner_BehavioralRisk"
        />
      </ContentRow>
    </Container>
  )
}

export default BehavioralRiskPlaceholder
